/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/templateSelect.scss';
import {Link, Loader, WithBackground} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import {defer} from 'lodash';

import React, {MouseEventHandler, useEffect, useMemo, useRef} from 'react';
import DefaultEmail from '../../../models/defaultEmailModel';
import EmailTemplate from '../../../models/emailTemplateModel';
import {EmailFlow} from '../../../util/interfaces';
import {BolmateButton} from '../../controls';
import ConfirmationMessage from '../../misc/messages/confirmationMessage';
import {TemplateTab} from './templateSelectController';

interface Props {
    onClose: MouseEventHandler;
    onChooseDefault: (defaultEmail?: DefaultEmail) => MouseEventHandler;
    onChooseTemplate: (template: EmailTemplate) => MouseEventHandler;
    onChangeTab: (tab: TemplateTab) => MouseEventHandler;
    onOpenTemplate: (template: EmailTemplate) => MouseEventHandler;
    onPreviewTemplate: (body: string, isHTML?: boolean, isLegacy?: boolean, bcc?: string, subject?: string) => void;
    onDeleteTemplate: (template: EmailTemplate) => MouseEventHandler;
    onToggleShowAll: MouseEventHandler;
    onDuplicateTemplate: (template: EmailTemplate) => MouseEventHandler;
    templates: EmailTemplate[];
    loading: boolean;
    active: boolean;
    activeTab: TemplateTab;
    currentTemplateId: string | null;
    flow: EmailFlow;
    showAll: boolean;
    defaultEmails: Array<DefaultEmail>;
}

export default function TemplateSelect(props: Props) {
    const {
        active, templates, onOpenTemplate, onChooseTemplate, onPreviewTemplate, onChooseDefault, onChangeTab,
        onClose, loading, activeTab, flow, currentTemplateId, onDeleteTemplate, showAll, onToggleShowAll,
        onDuplicateTemplate, defaultEmails
    } = props;
    const showDefaultEmails: Array<DefaultEmail> = useMemo(() => {
        if (showAll) return defaultEmails;
        return defaultEmails.filter((defaultEmail: DefaultEmail) => defaultEmail.emailFlows.includes(flow));
    }, [showAll, defaultEmails, flow]);
    const currentTemplate = useRef<HTMLDivElement | null>(null);

    function onShowDefaultPreview(defaultEmail: DefaultEmail) {
        return function () {
            if (!defaultEmail.previewURL) return;
            onPreviewTemplate(defaultEmail.previewURL);
        };
    }

    function onShowPreviewCustom(template: EmailTemplate) {
        return function () {
            let body: string, isLegacy: boolean = false;
            if (template.htmlBody.length) body = template.htmlBody;
            else {
                body = template.body;
                isLegacy = true;
            }
            onPreviewTemplate(body, true, isLegacy, template.bcc, template.subject);
        };
    }

    function scrollInView() {
        currentTemplate.current?.scrollIntoView({behavior: 'smooth'});
    }

    function renderTab(label: string, tab: TemplateTab) {
        const isActive = activeTab === tab;
        return (
            <div className={classNames('tab-select', isActive && 'active')} onClick={onChangeTab(tab)}>
                <label className='tab-label'>{label}</label>
            </div>
        );
    }

    function renderButton(label: string, onClick: MouseEventHandler, primary: boolean = true) {
        return (
            <div className={classNames('template-button', primary && 'primary')} onClick={onClick}>
                <label className='button-label'>{label}</label>
            </div>
        );
    }

    function renderDefaultTab() {
        return (
            <div className='tab default'>
                <div className='labels'>
                    <label>Je kunt een template kiezen, aanpassen en voorvertonen.</label>
                    <ConfirmationMessage>
                        <label>Conversietip: Voeg persoonlijke foto's toe om je te benadrukken als kleine ondernemer;
                            dit vergroot de kans op reviews. <Link
                                route='https://bolmate.nl/dit-zijn-de-best-presterende-e-mail-campagnes-voor-review-op-bol-com-tips/'>Lees
                                meer</Link></label>
                    </ConfirmationMessage>
                </div>
                <div className='templates'>
                    <Loader active={loading}/>
                    {showDefaultEmails.map(renderDefaultEmail)}
                    <div className='empty-template' onClick={onChooseDefault()}>
                        <label className='empty-label'>Begin leeg</label>
                    </div>
                </div>
            </div>
        );
    }

    function renderDefaultEmail(defaultEmail: DefaultEmail, index: number) {
        return (
            <div key={index} className='template'>
                <div className='preview-container'>
                    <img src={defaultEmail.previewURL ?? ''} alt={defaultEmail.templateName} className='preview'/>
                </div>
                <label className='template-name'>{defaultEmail.templateName}</label>
                <div className='options-overlay'>
                    {renderButton('Kiezen & Aanpassen', onChooseDefault(defaultEmail), true)}
                    {renderButton('Preview', onShowDefaultPreview(defaultEmail), false)}
                </div>
            </div>
        );
    }

    function renderCustomTab() {
        return (
            <div className='tab custom'>
                <div className='toolbar'>
                    <BolmateButton onClick={onChangeTab(TemplateTab.DEFAULT)} label='+ Nieuwe E-mail Template maken'/>
                </div>
                <div className='row head'>
                    <label>EMAIL TITEL</label>
                    <label>LAATST BIJGEWERKT</label>
                </div>
                <div className='templates-container'>
                    <div className='templates'>
                        <Loader active={loading}/>
                        {templates.map(renderCustomTemplate)}
                    </div>
                </div>
            </div>
        );
    }

    function renderCustomTemplate(template: EmailTemplate, index: number) {
        const isSelected = currentTemplateId === template.id;
        return (
            <div className={classNames('row', isSelected && 'current')} key={index} tabIndex={-1}
                 ref={isSelected ? currentTemplate : undefined}>
                <label>{template.name}{isSelected && <>&nbsp;<span
                    className='current-label'>(huidig)</span></>}</label>
                <label>{template.updatedAtMoment?.format('DD-MM-YYYY HH:mm:ss') ?? '-'}</label>
                <div className='options-container'>
                    {!isSelected &&
                        <label className='option' onClick={onChooseTemplate(template)}>Kiezen</label>}
                    <label className='option'
                           onClick={onShowPreviewCustom(template)}>Bekijken</label>
                    <label className='option' onClick={onOpenTemplate(template)}>Aanpassen</label>
                    <label className='option' onClick={onDuplicateTemplate(template)}>Kopiëren</label>
                    {!isSelected && <label className='option red'
                                           onClick={onDeleteTemplate(template)}>Verwijderen</label>}
                </div>
            </div>
        );
    }

    function renderAllToggle() {
        if (activeTab !== TemplateTab.DEFAULT) return null;
        const text = showAll ? 'Specifieke templates tonen' : 'Alle templates tonen';
        return <Link className='all-toggle' onClick={onToggleShowAll} underline>{text}</Link>;
    }

    useEffect(() => {
        if (active && activeTab === TemplateTab.CUSTOM && templates.length)
            defer(scrollInView);
    }, [activeTab, active, templates]);

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='template-select default-popup'>
                <div className='header-container'>
                    <label className='header-title'>Kies jouw E-mail Template</label>
                </div>
                <div className='body-container'>
                    <div className='tabs-container'>
                        {renderTab('Template Layouts', TemplateTab.DEFAULT)}
                        {renderTab('Opgeslagen Templates', TemplateTab.CUSTOM)}
                    </div>
                    {activeTab === TemplateTab.DEFAULT ? renderDefaultTab() : renderCustomTab()}
                    {renderAllToggle()}
                </div>
            </div>
        </WithBackground>
    );
}